import { memo } from 'react'
import type { TSong, TDraftReviewSong, TEnumItem } from '@modules/types'
import { isPrintOnly, isDigitalOnly, isBoth } from '@modules/api-core'
import { SingleFeeQuotedPrice, DualFeeQuotedPrice } from './components'
import type { FeeQuotedPriceControl } from '../../types'

type FeeQuotedPriceViewProps = {
  song: TSong
  songHasStatus: boolean
  initialLoading: boolean
  draftReviewSong: TDraftReviewSong
  distributionMethod: number
  enums: TEnumItem[]
  isBlocked?: boolean
  digitalCounterOfferFeeValidationError?: string
  printCounterOfferFeeValidationError?: string
  approvalDecision?: {
    isApproved: number
  } | null
  processingSongId?: number | null
  control: FeeQuotedPriceControl
}

export const FeeQuotedPrice = memo((props: FeeQuotedPriceViewProps) => {
  const {
    editingState,
    feeQuotedPriceValues,
    isQuotedPriceUpdating,
    handleFeeQuotedPriceEdit,
    handleFeeQuotedPriceChange,
    handleFeeQuotedPriceSave,
    handleDualFeeQuotedPriceSave,
    handleFeeQuotedPriceCancel,
  } = props.control

  const {
    song,
    songHasStatus,
    initialLoading,
    draftReviewSong,
    distributionMethod,
    enums,
    isBlocked,
    digitalCounterOfferFeeValidationError,
    printCounterOfferFeeValidationError,
    approvalDecision,
    processingSongId,
  } = props

  if (isPrintOnly(distributionMethod)) {
    return (
      <SingleFeeQuotedPrice
        song={song}
        songHasStatus={songHasStatus}
        isLoading={isQuotedPriceUpdating}
        initialLoading={initialLoading}
        editingState={editingState}
        feeQuotedPriceValues={feeQuotedPriceValues}
        draftReviewSong={draftReviewSong}
        distributionMethod={distributionMethod}
        feeType="print"
        label="Fee quoted price (Print)"
        enums={enums}
        onEdit={handleFeeQuotedPriceEdit}
        onChange={handleFeeQuotedPriceChange}
        onSave={handleFeeQuotedPriceSave}
        onCancel={handleFeeQuotedPriceCancel}
        isBlocked={isBlocked}
        processingSongId={processingSongId}
        validationError={printCounterOfferFeeValidationError}
        approvalDecision={approvalDecision}
      />
    )
  }
  if (isDigitalOnly(distributionMethod)) {
    return (
      <SingleFeeQuotedPrice
        song={song}
        songHasStatus={songHasStatus}
        isLoading={isQuotedPriceUpdating}
        initialLoading={initialLoading}
        editingState={editingState}
        feeQuotedPriceValues={feeQuotedPriceValues}
        draftReviewSong={draftReviewSong}
        distributionMethod={distributionMethod}
        feeType="digital"
        label="Fee quoted price (Digital)"
        enums={enums}
        onEdit={handleFeeQuotedPriceEdit}
        onChange={handleFeeQuotedPriceChange}
        onSave={handleFeeQuotedPriceSave}
        onCancel={handleFeeQuotedPriceCancel}
        isBlocked={isBlocked}
        processingSongId={processingSongId}
        validationError={digitalCounterOfferFeeValidationError}
        approvalDecision={approvalDecision}
      />
    )
  }
  if (isBoth(distributionMethod)) {
    return (
      <DualFeeQuotedPrice
        song={song}
        songHasStatus={songHasStatus}
        isLoading={isQuotedPriceUpdating}
        initialLoading={initialLoading}
        editingState={editingState}
        feeQuotedPriceValues={feeQuotedPriceValues}
        draftReviewSong={draftReviewSong}
        distributionMethod={distributionMethod}
        enums={enums}
        onEdit={handleFeeQuotedPriceEdit}
        onChange={handleFeeQuotedPriceChange}
        onDualSave={handleDualFeeQuotedPriceSave}
        onCancel={handleFeeQuotedPriceCancel}
        isBlocked={isBlocked}
        processingSongId={processingSongId}
        digitalCounterOfferFeeValidationError={digitalCounterOfferFeeValidationError}
        printCounterOfferFeeValidationError={printCounterOfferFeeValidationError}
        approvalDecision={approvalDecision}
      />
    )
  }

  return (
    <SingleFeeQuotedPrice
      song={song}
      songHasStatus={songHasStatus}
      isLoading={isQuotedPriceUpdating}
      initialLoading={initialLoading}
      editingState={editingState}
      feeQuotedPriceValues={feeQuotedPriceValues}
      draftReviewSong={draftReviewSong}
      distributionMethod={distributionMethod}
      feeType="print"
      label="Fee quoted price"
      enums={enums}
      onEdit={handleFeeQuotedPriceEdit}
      onChange={handleFeeQuotedPriceChange}
      onSave={handleFeeQuotedPriceSave}
      onCancel={handleFeeQuotedPriceCancel}
      isBlocked={isBlocked}
      processingSongId={processingSongId}
      validationError={printCounterOfferFeeValidationError}
      approvalDecision={approvalDecision}
    />
  )
})
